import { useEffect, useState } from "react";
import { http, inr } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function BookingCalendar() {
  const [data, setData] = useState({ rooms: [], bookings: [] });
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [sel, setSel] = useState(null);

  useEffect(() => { http.get("/admin/calendar").then((r) => setData(r.data)).catch(() => {}); }, []);

  const [y, m] = ym.split("-").map(Number);
  const days = new Date(y, m, 0).getDate();
  const shift = (n) => {
    const d = new Date(y, m - 1 + n, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    setSel(null);
  };
  const dayStr = (d) => `${ym}-${String(d).padStart(2, "0")}`;
  const bookingFor = (roomId, ds) =>
    data.bookings.find((b) => (b.room_ids || []).includes(roomId) && b.check_in_date <= ds && b.check_out_date > ds);

  const colors = { CONFIRMED: "var(--terracotta)", CHECKED_IN: "var(--pine)", DRAFT: "#B9AE9F" };

  return (
    <div className="space-y-4" data-testid="booking-calendar">
      <div className="flex items-center gap-3">
        <button data-testid="cal-prev" onClick={() => shift(-1)} className="btn-outline" style={{ padding: "6px 12px" }}><ChevronLeft size={16} /></button>
        <div className="font-serif text-2xl" data-testid="cal-month">{new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" })}</div>
        <button data-testid="cal-next" onClick={() => shift(1)} className="btn-outline" style={{ padding: "6px 12px" }}><ChevronRight size={16} /></button>
        <div className="flex gap-3 ml-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: "var(--terracotta)" }} /> Confirmed</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: "var(--pine)" }} /> Checked in</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: "#B9AE9F" }} /> Draft</span>
        </div>
      </div>
      <div className="card overflow-x-auto" style={{ padding: 12 }}>
        <table className="text-xs" style={{ borderCollapse: "collapse", minWidth: days * 34 + 160 }}>
          <thead>
            <tr>
              <th className="p-1 text-left" style={{ minWidth: 150 }}>Room</th>
              {Array.from({ length: days }, (_, i) => (
                <th key={i} className="p-1 text-center" style={{ color: "var(--muted)", fontWeight: 500, minWidth: 34 }}>{i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rooms.map((room) => (
              <tr key={room.id}>
                <td className="p-1 font-medium whitespace-nowrap">{room.name}</td>
                {Array.from({ length: days }, (_, i) => {
                  const ds = dayStr(i + 1);
                  const b = bookingFor(room.id, ds);
                  return (
                    <td key={i} className="p-0">
                      <button
                        data-testid={b ? `cal-cell-${room.id}-${ds}` : undefined}
                        onClick={() => b && setSel(b)}
                        title={b ? `${b.full_name} · ${b.booking_reference}` : ""}
                        style={{
                          width: "100%", height: 26, display: "block",
                          background: b ? colors[b.status] || colors.DRAFT : "transparent",
                          borderRadius: 4, opacity: b ? 0.9 : 1, cursor: b ? "pointer" : "default",
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="card fadein" data-testid="cal-selected">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-serif text-2xl">{sel.booking_reference} — {sel.full_name}</div>
              <div className="text-sm" style={{ color: "var(--muted)" }}>
                {sel.check_in_date} → {sel.check_out_date} · {sel.total_pax} guests · {inr(sel.booking_amount)} · source {sel.source}
              </div>
            </div>
            <span className={sel.status === "DRAFT" ? "badge-terracotta" : "badge-pine"}>{sel.status}</span>
          </div>
        </div>
      )}
    </div>
  );
}
